import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { SidebarMenuButton, SidebarMenuItem } from '@monksflow/monks-ui';

export interface FeatureSidebarNavItemProps {
  /** The icon element to render (e.g. lucide-react icon). */
  icon: ReactNode;
  /** Label text shown beside the icon. */
  label: string;
  /** Whether this nav item is currently active. */
  active?: boolean;
  /** When true, the item is visible but cannot be interacted with. */
  disabled?: boolean;
  /** Click handler for navigation. */
  onClick: () => void;
}

// Stagger item spec — designer-tuned: each item travels a bit slower
// (0.4 s) so the entrance reads even on quick page transitions.
// Triggered by the parent FeatureSidebarNav's container context.
const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] as const },
  },
};

/** Single navigation item for `FeatureSidebar`. */
export function FeatureSidebarNavItem({
  icon,
  label,
  active = false,
  disabled = false,
  onClick,
}: FeatureSidebarNavItemProps) {
  const iconColorClass = active ? 'text-primary' : 'text-foreground-subtle';
  const labelColorClass = active ? 'font-medium text-primary' : 'text-foreground font-normal';

  return (
    <SidebarMenuItem>
      <motion.div variants={itemVariants}>
        <SidebarMenuButton
          orientation="horizontal"
          isActive={active}
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          aria-disabled={disabled || undefined}
          aria-current={active ? 'page' : undefined}
          className={`hover:bg-sidebar-accent/50 ${
            disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
          }`}
        >
          <span
            className={`flex size-md mr-1 shrink-0 items-center justify-center ${iconColorClass}`}
          >
            {icon}
          </span>
          <span className={labelColorClass}>{label}</span>
        </SidebarMenuButton>
      </motion.div>
    </SidebarMenuItem>
  );
}
